import { formatBrlCurrency } from "./price";
import {
  DEFAULT_PAYMENT_METHOD_ID,
  getPaymentMethod,
  type PaymentMethodId,
} from "./paymentMethods";
import {
  DEFAULT_WHATSAPP_CART_MESSAGE_TEMPLATE,
  DEFAULT_WHATSAPP_PRODUCT_MESSAGE_TEMPLATE,
  renderMessageTemplate,
} from "./whatsappTemplates";

export {
  CART_LINE_TEMPLATE_VARIABLES,
  DEFAULT_WHATSAPP_PRODUCT_MESSAGE_TEMPLATE,
  DEFAULT_WHATSAPP_CART_MESSAGE_TEMPLATE,
  WHATSAPP_TEMPLATE_VARIABLES,
  renderMessageTemplate,
} from "./whatsappTemplates";

export const MAX_CART_ITEMS = 20;

const WHATSAPP_FORMAT_CHARS = /[*_~`]/g;

/** Strip characters that would break WhatsApp *bold* / _italic_ markers in user text. */
export function sanitizeWhatsAppText(text: string): string {
  return text.replace(WHATSAPP_FORMAT_CHARS, "").replace(/\s+/g, " ").trim();
}

/** Bold for user-provided text (names); strips characters that break WA formatting. */
export function waBold(text: string): string {
  const safe = sanitizeWhatsAppText(text);
  return safe ? `*${safe}*` : "";
}

/** Bold for system-generated strings (e.g. formatted prices). */
export function waBoldLiteral(text: string): string {
  return `*${text}*`;
}

/** Italic for system-generated strings (e.g. line prices). */
export function waItalicLiteral(text: string): string {
  return `_${text}_`;
}

export function normalizeWhatsAppPhone(phone: string): string {
  return phone.replace(/\D/g, "");
}

export function buildWhatsAppUrl(phone: string, message: string): string {
  const digits = normalizeWhatsAppPhone(phone);
  if (!digits) throw new Error("WhatsApp phone required");
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

export function buildProductMessage(params: {
  productName: string;
  priceCents: number;
  productUrl: string;
  storeName?: string;
  template?: string | null;
  paymentMethodId?: PaymentMethodId;
}): string {
  const paymentMethod = getPaymentMethod(params.paymentMethodId ?? DEFAULT_PAYMENT_METHOD_ID);

  return renderMessageTemplate(
    params.template,
    {
      store_name: sanitizeWhatsAppText(params.storeName ?? ""),
      product_name: sanitizeWhatsAppText(params.productName),
      product_price: formatBrlCurrency(params.priceCents),
      product_url: params.productUrl,
      cart_items: "",
      cart_total: "",
      payment_method: paymentMethod.formatForWhatsApp(),
    },
    DEFAULT_WHATSAPP_PRODUCT_MESSAGE_TEMPLATE
  );
}

function formatCartLine(
  index: number,
  name: string,
  priceCents: number,
  quantity = 1
): string {
  const label = waBold(name);
  const price = waItalicLiteral(formatBrlCurrency(priceCents));
  const qtyLabel = quantity > 1 ? ` × ${quantity}` : "";
  return `${index + 1}. ${label}${qtyLabel} — ${price}`;
}

function buildCartItemsBlock(
  items: Array<{ name: string; priceCents: number; quantity?: number }>
): string {
  if (items.length === 0) return "";
  return items
    .map((item, i) =>
      formatCartLine(i, item.name, item.priceCents, item.quantity ?? 1)
    )
    .join("\n");
}

export function buildCartMessage(params: {
  items: Array<{ name: string; priceCents: number; quantity?: number }>;
  totalCents: number;
  storeName?: string;
  template?: string | null;
  paymentMethodId?: PaymentMethodId;
}): string {
  const paymentMethod = getPaymentMethod(params.paymentMethodId ?? DEFAULT_PAYMENT_METHOD_ID);
  const globalVariables = {
    store_name: sanitizeWhatsAppText(params.storeName ?? ""),
    product_name: "",
    product_price: "",
    product_url: "",
    cart_items: buildCartItemsBlock(params.items),
    cart_total: formatBrlCurrency(params.totalCents),
    payment_method: paymentMethod.formatForWhatsApp(),
  };

  return renderMessageTemplate(
    params.template,
    globalVariables,
    DEFAULT_WHATSAPP_CART_MESSAGE_TEMPLATE,
    params.items
  );
}
